import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { queryKeys } from '@/lib/query-client';
import type {
  DailyReport,
  DailyReportAmendment,
  CreateDailyReportPayload,
  UpdateDailyReportPayload,
  CreateDailyReportCrewPayload,
  DailyReportCrew,
  DelayEntry,
  CreateDelayEntryPayload,
  DailyReportPhoto,
  PaginatedResponse,
} from '@/lib/types';

const API = '/api/v1/projects';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface DailyReportFilters {
  page?: number;
  page_size?: number;
  project?: number;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Daily Report Hooks ─────────────────────────────────────────────────────

export function useDailyReports(filters: DailyReportFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.dailyReports.list(clean),
    queryFn: async (): Promise<PaginatedResponse<DailyReport>> => {
      const response = await apiClient.get<PaginatedResponse<DailyReport>>(
        `${API}/daily-reports/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useDailyReport(id: number) {
  return useQuery({
    queryKey: queryKeys.dailyReports.detail(id),
    queryFn: async (): Promise<DailyReport> => {
      const response = await apiClient.get<DailyReport>(
        `${API}/daily-reports/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateDailyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateDailyReportPayload): Promise<DailyReport> => {
      const response = await apiClient.post<DailyReport>(
        `${API}/daily-reports/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.dailyReports.all });
    },
  });
}

export function useUpdateDailyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateDailyReportPayload;
    }): Promise<DailyReport> => {
      const response = await apiClient.patch<DailyReport>(
        `${API}/daily-reports/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.dailyReports.all });
      qc.invalidateQueries({
        queryKey: queryKeys.dailyReports.detail(variables.id),
      });
    },
  });
}

export function useDeleteDailyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${API}/daily-reports/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.dailyReports.all });
    },
  });
}

// ─── Crew Entry Hooks ───────────────────────────────────────────────────────

export function useAddCrewEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CreateDailyReportCrewPayload
    ): Promise<DailyReportCrew> => {
      const response = await apiClient.post<DailyReportCrew>(
        `${API}/daily-report-crew/`,
        data
      );
      return response.data;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({
        queryKey: queryKeys.dailyReports.detail(result.daily_report),
      });
    },
  });
}

// ─── Delay Entry Hooks ──────────────────────────────────────────────────────

export function useAddDelayEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CreateDelayEntryPayload
    ): Promise<DelayEntry> => {
      const response = await apiClient.post<DelayEntry>(
        `${API}/delays/`,
        data
      );
      return response.data;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({
        queryKey: queryKeys.dailyReports.detail(result.daily_report),
      });
      qc.invalidateQueries({ queryKey: queryKeys.delayEntries.all });
    },
  });
}

// ─── Photo Hooks ────────────────────────────────────────────────────────────

export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dailyReportId,
      file,
      caption,
      delayEntryId,
      captureLatitude,
      captureLongitude,
      capturedAt,
    }: {
      dailyReportId: number;
      file: File;
      caption?: string;
      delayEntryId?: number | null;
      captureLatitude?: string | number | null;
      captureLongitude?: string | number | null;
      capturedAt?: string | null;
    }): Promise<DailyReportPhoto> => {
      const form = new FormData();
      form.append('daily_report', String(dailyReportId));
      form.append('image', file);
      if (caption) form.append('caption', caption);
      if (delayEntryId != null) form.append('delay_entry', String(delayEntryId));
      if (captureLatitude != null) {
        form.append('capture_latitude', String(captureLatitude));
      }
      if (captureLongitude != null) {
        form.append('capture_longitude', String(captureLongitude));
      }
      if (capturedAt) {
        form.append('captured_at', capturedAt);
      }
      const response = await apiClient.post<DailyReportPhoto>(
        `${API}/daily-report-photos/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({
        queryKey: queryKeys.dailyReports.detail(result.daily_report),
      });
    },
  });
}

export function useRemovePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: number;
      dailyReportId: number;
    }): Promise<void> => {
      await apiClient.delete(`${API}/daily-report-photos/${id}/`);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.dailyReports.detail(variables.dailyReportId),
      });
    },
  });
}

// ─── Amend Hook ─────────────────────────────────────────────────────────────

export function useAmendDailyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
      changes,
    }: {
      id: number;
      reason: string;
      changes: Record<string, unknown>;
    }): Promise<DailyReportAmendment> => {
      const response = await apiClient.post<DailyReportAmendment>(
        `${API}/daily-reports/${id}/amend/`,
        { reason, changes }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.dailyReports.all });
    },
  });
}

// ─── Acknowledge Hook ───────────────────────────────────────────────────────

export function useAcknowledgeDailyReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      acknowledged_by_name,
      acknowledged_by_email,
    }: {
      id: number;
      acknowledged_by_name: string;
      acknowledged_by_email: string;
    }): Promise<{ detail: string; client_acknowledged_at: string }> => {
      const response = await apiClient.post<{
        detail: string;
        client_acknowledged_at: string;
      }>(`${API}/daily-reports/${id}/acknowledge/`, {
        acknowledged_by_name,
        acknowledged_by_email,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.dailyReports.all });
      qc.invalidateQueries({
        queryKey: queryKeys.dailyReports.detail(variables.id),
      });
    },
  });
}

// ─── Export PDF ─────────────────────────────────────────────────────────────

export function useExportDailyReportPdf() {
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const response = await apiClient.get(
        `${API}/daily-reports/${id}/pdf/`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFromContentDisposition(
        response.headers['content-disposition'],
        `DailyReport-${id}.pdf`,
      );
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}
